import api from '../../../lib/api';
import type {
  ChatwootChannel,
  ChatwootInboundEventSummary,
  CreateChatwootChannelRequest,
  CreateChatwootChannelResponse,
  UpdateChatwootChannelRequest,
} from '@workspace/types';

type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

function requireData<T>(response: { data: ApiResponse<T> }, fallbackError: string): T {
  if (!response.data.success || response.data.data === undefined) {
    throw new Error(response.data.error || fallbackError);
  }
  return response.data.data;
}

export type ChatwootConversationSummary = {
  id: string;
  externalRef: string | null;
  handoffStatus: string;
  lastMessageAt: string;
  messageCount: number;
  handoffAt: string | null;
};

export const chatwootAdminApi = {
  async listChannels(): Promise<ChatwootChannel[]> {
    const response = await api.get<ApiResponse<ChatwootChannel[]>>('/admin/chatwoot/channels');
    return requireData(response, 'No se pudieron cargar los canales Chatwoot');
  },

  async getChannel(id: string): Promise<ChatwootChannel | null> {
    try {
      const response = await api.get<ApiResponse<ChatwootChannel>>(`/admin/chatwoot/channels/${id}`);
      return requireData(response, 'Canal no encontrado');
    } catch {
      return null;
    }
  },

  async createChannel(
    payload: CreateChatwootChannelRequest,
  ): Promise<CreateChatwootChannelResponse> {
    const response = await api.post<ApiResponse<CreateChatwootChannelResponse>>(
      '/admin/chatwoot/channels',
      payload,
    );
    return requireData(response, 'No se pudo crear el canal Chatwoot');
  },

  async updateChannel(id: string, payload: UpdateChatwootChannelRequest): Promise<ChatwootChannel> {
    const response = await api.patch<ApiResponse<ChatwootChannel>>(
      `/admin/chatwoot/channels/${id}`,
      payload,
    );
    return requireData(response, 'No se pudo actualizar el canal Chatwoot');
  },

  async deleteChannel(id: string): Promise<void> {
    await api.delete(`/admin/chatwoot/channels/${id}`);
  },

  async healthCheck(id: string): Promise<{ status: string; message: string }> {
    const response = await api.post<ApiResponse<{ status: string; message: string }>>(
      `/admin/chatwoot/channels/${id}/health-check`,
    );
    return requireData(response, 'No se pudo ejecutar el health check');
  },

  async testMessage(
    id: string,
    payload: { externalConversationId: string; content: string },
  ): Promise<{ messageId: number }> {
    const response = await api.post<ApiResponse<{ messageId: number }>>(
      `/admin/chatwoot/channels/${id}/test-message`,
      payload,
    );
    return requireData(response, 'No se pudo enviar el mensaje de prueba');
  },

  async listConversations(
    channelId: string,
    params?: { limit?: number },
  ): Promise<ChatwootConversationSummary[]> {
    const response = await api.get<ApiResponse<ChatwootConversationSummary[]>>(
      `/admin/chatwoot/channels/${channelId}/conversations`,
      { params },
    );
    return requireData(response, 'No se pudieron cargar las conversaciones');
  },

  async listEvents(
    channelId: string,
    params?: { status?: 'processed' | 'failed'; limit?: number },
  ): Promise<ChatwootInboundEventSummary[]> {
    const response = await api.get<ApiResponse<ChatwootInboundEventSummary[]>>(
      `/admin/chatwoot/channels/${channelId}/events`,
      { params },
    );
    return requireData(response, 'No se pudieron cargar los eventos');
  },
};
